import { useCallback, useEffect, useState } from 'react'
import { Alert, Button, Empty, Form, Input, InputNumber, Modal, Popconfirm, Select, Space, Spin, Switch, Table, Tag, Tooltip, Typography, message } from 'antd'
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons'
import { slaRulesApi, type SlaRule, type SlaStatusGroup } from '../services/slaRulesApi'
import { crmIntegrationApi, type CrmIntegrationSettings } from '../services/crmIntegrationApi'

const { Text } = Typography

// Срок правила: минуты показываем только когда они заданы.
function formatDelay(hours: number, minutes: number): string {
    const parts: string[] = []
    if (hours) parts.push(`${hours} ч`)
    if (minutes) parts.push(`${minutes} мин`)
    return parts.join(' ') || '0 мин'
}

function extractError(error: unknown): string {
    const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
    if (typeof detail === 'string') return detail
    return 'Не удалось выполнить действие'
}

// Подраздел «SLA»: сколько лид может висеть в статусе и что делать, когда срок
// вышел - перевести в другой статус и/или поставить автозадачу ответственному.
export default function SlaRulesSettings() {
    const [rules, setRules] = useState<SlaRule[]>([])
    const [groups, setGroups] = useState<SlaStatusGroup[]>([])
    const [loading, setLoading] = useState(true)
    // Правило по первому сообщению живёт в настройках интеграции: оно пишет
    // статус в их базу под тем же переключателем. Показываем его здесь, рядом с
    // остальными правилами SLA.
    const [crmSettings, setCrmSettings] = useState<CrmIntegrationSettings | null>(null)
    const [saving, setSaving] = useState(false)
    const [formOpen, setFormOpen] = useState(false)
    const [editing, setEditing] = useState<SlaRule | null>(null)
    const [form] = Form.useForm()
    const createTask = Form.useWatch('create_task', form)

    const load = useCallback(async () => {
        setLoading(true)
        try {
            const [rulesResponse, statusesResponse, crmResponse] = await Promise.all([
                slaRulesApi.getRules(),
                slaRulesApi.getStatuses(),
                crmIntegrationApi.getSettings(),
            ])
            setRules(rulesResponse.data)
            setGroups(statusesResponse.data.groups)
            setCrmSettings(crmResponse.data)
        } catch (error) {
            message.error(extractError(error))
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        load()
    }, [load])

    const statusOptions = groups.map((group) => ({
        label: group.group,
        options: group.reasons.map((reason) => ({ value: reason.code, label: reason.label })),
    }))

    const openCreate = () => {
        setEditing(null)
        form.resetFields()
        form.setFieldsValue({
            hours: 72,
            minutes: 0,
            create_task: true,
            task_title: 'Проверь чат с агентом и актуализируй статус',
            task_text: 'Поставь задачу, чтобы не забыть про лида',
        })
        setFormOpen(true)
    }

    const openEdit = (rule: SlaRule) => {
        setEditing(rule)
        form.setFieldsValue({
            title: rule.title || '',
            from_status: rule.from_status,
            hours: rule.hours,
            minutes: rule.minutes || 0,
            to_status: rule.to_status || undefined,
            create_task: rule.create_task,
            task_title: rule.task_title || '',
            task_text: rule.task_text || '',
        })
        setFormOpen(true)
    }

    const submit = async () => {
        try {
            const values = await form.validateFields()
            setSaving(true)
            const payload = {
                title: (values.title || '').trim() || null,
                from_status: values.from_status,
                hours: values.hours ?? 0,
                minutes: values.minutes ?? 0,
                to_status: values.to_status || null,
                create_task: Boolean(values.create_task),
                task_title: (values.task_title || '').trim() || null,
                task_text: (values.task_text || '').trim() || null,
            }
            if (editing) await slaRulesApi.updateRule(editing.id, payload)
            else await slaRulesApi.createRule(payload)
            message.success(editing ? 'Правило сохранено' : 'Правило создано')
            setFormOpen(false)
            await load()
        } catch (error) {
            if (error && typeof error === 'object' && 'errorFields' in error) return
            message.error(extractError(error))
        } finally {
            setSaving(false)
        }
    }

    const saveCrmSettings = async (patch: Parameters<typeof crmIntegrationApi.updateSettings>[0]) => {
        const previous = crmSettings
        setCrmSettings((current) => (current ? { ...current, ...patch } as CrmIntegrationSettings : current))
        try {
            await crmIntegrationApi.updateSettings(patch)
        } catch (error) {
            setCrmSettings(previous)
            message.error(extractError(error))
        }
    }

    const toggle = async (rule: SlaRule, enabled: boolean) => {
        setRules((items) => items.map((item) => (item.id === rule.id ? { ...item, enabled } : item)))
        try {
            await slaRulesApi.updateRule(rule.id, { enabled })
        } catch (error) {
            setRules((items) => items.map((item) => (item.id === rule.id ? { ...item, enabled: !enabled } : item)))
            message.error(extractError(error))
        }
    }

    const remove = async (rule: SlaRule) => {
        try {
            await slaRulesApi.deleteRule(rule.id)
            message.success('Правило удалено')
            await load()
        } catch (error) {
            message.error(extractError(error))
        }
    }

    const columns = [
        {
            title: '',
            key: 'enabled',
            width: 60,
            render: (_: unknown, rule: SlaRule) => (
                <Switch size="small" checked={rule.enabled} onChange={(checked) => toggle(rule, checked)} />
            ),
        },
        {
            title: 'Условие',
            key: 'condition',
            render: (_: unknown, rule: SlaRule) => (
                <Space direction="vertical" size={2}>
                    {rule.title && <Text strong>{rule.title}</Text>}
                    <Text>
                        В статусе <Tag>{rule.from_status_label}</Tag> дольше <b>{formatDelay(rule.hours, rule.minutes)}</b>
                    </Text>
                </Space>
            ),
        },
        {
            title: 'Что делаем',
            key: 'action',
            render: (_: unknown, rule: SlaRule) => (
                <Space direction="vertical" size={4}>
                    {rule.to_status_label && (
                        <Text>Перевести в <Tag color="blue">{rule.to_status_label}</Tag></Text>
                    )}
                    {rule.create_task && (
                        <Space direction="vertical" size={0}>
                            <Text>Автозадача ответственному:</Text>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                «{rule.task_title || 'Проверь чат с агентом и актуализируй статус'}»
                            </Text>
                        </Space>
                    )}
                </Space>
            ),
        },
        {
            title: '',
            key: 'actions',
            width: 100,
            render: (_: unknown, rule: SlaRule) => (
                <Space size={4}>
                    <Tooltip title="Изменить">
                        <Button type="text" icon={<EditOutlined />} onClick={() => openEdit(rule)} />
                    </Tooltip>
                    <Popconfirm title="Удалить правило?" okText="Удалить" cancelText="Отмена" onConfirm={() => remove(rule)}>
                        <Tooltip title="Удалить">
                            <Button type="text" danger icon={<DeleteOutlined />} />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            ),
        },
    ]

    return (
        <Spin spinning={loading}>
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <Alert
                    type="info"
                    showIcon
                    message="Как это работает"
                    description={
                        <Space direction="vertical" size={4}>
                            <Text>
                                Правило смотрит, сколько лид висит в статусе CRM Wiseway. Время считается по их же
                                отметке смены статуса, своей копии статуса мы не храним. Когда срок вышел — лид
                                переводится в выбранный статус и/или ответственному ставится автозадача.
                            </Text>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                Смена статуса уходит в CRM Wiseway и работает только вместе с переключателем
                                «Передавать статусы в CRM Wiseway» в разделе интеграции. Автозадачи от него
                                не зависят. Каждое правило срабатывает на лида один раз за вход в статус.
                            </Text>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                В тексте автозадачи можно подставлять: {'{lead}'} — название лида, {'{crm_id}'} — ID
                                карточки CRM WW, {'{geo}'} — гео, {'{source}'} — источник.
                            </Text>
                        </Space>
                    }
                />

                <div>
                    <Space size={12} align="start">
                        <Switch
                            checked={crmSettings?.sla_no_answer_enabled}
                            onChange={(checked) => saveCrmSettings({ sla_no_answer_enabled: checked })}
                        />
                        <div>
                            <div>SLA по первому сообщению</div>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                Отдельное правило: если лид не ответил в течение срока с момента отправки первого
                                сообщения, его карточка в CRM Wiseway переводится в «Нет ответа на первое
                                сообщение». Ответил позже — статус меняется на «Ведется сбор информации». Считается
                                от момента отправки, а не от смены статуса, поэтому живёт отдельно от правил ниже.
                            </Text>
                            {/* Срок виден всегда, а не только при включённом правиле: его
                                задают заранее, до того как правило включат. */}
                            <Space size={8} align="center" style={{ marginTop: 10 }} wrap>
                                <span>Срок молчания</span>
                                <InputNumber
                                    min={0}
                                    max={720}
                                    addonAfter="ч"
                                    value={crmSettings?.sla_no_answer_hours}
                                    onChange={(value) => value !== null && saveCrmSettings({ sla_no_answer_hours: Number(value) })}
                                />
                                <InputNumber
                                    min={0}
                                    max={59}
                                    addonAfter="мин"
                                    value={crmSettings?.sla_no_answer_minutes}
                                    onChange={(value) => value !== null && saveCrmSettings({ sla_no_answer_minutes: Number(value) })}
                                />
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    по умолчанию 48 часов; минуты можно не заполнять
                                </Text>
                            </Space>
                        </div>
                    </Space>
                </div>

                <Space style={{ width: '100%', justifyContent: 'space-between' }} align="center">
                    <Text strong>Правила по времени в статусе</Text>
                    <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Добавить правило</Button>
                </Space>

                <Table
                    rowKey="id"
                    size="middle"
                    columns={columns}
                    dataSource={rules}
                    pagination={false}
                    locale={{ emptyText: <Empty description="Правил пока нет" /> }}
                />
            </Space>

            <Modal
                title={editing ? `Правило #${editing.id}` : 'Новое правило SLA'}
                open={formOpen}
                onCancel={() => setFormOpen(false)}
                onOk={submit}
                okText={editing ? 'Сохранить' : 'Создать'}
                cancelText="Отмена"
                confirmLoading={saving}
                width={620}
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="title" label="Название правила">
                        <Input placeholder="Необязательно: например, «Оффер без решения»" />
                    </Form.Item>

                    <Space size={12} style={{ width: '100%' }} align="start" wrap>
                        <Form.Item
                            name="from_status"
                            label="Лид в статусе"
                            rules={[{ required: true, message: 'Выберите статус' }]}
                            style={{ minWidth: 280 }}
                        >
                            <Select showSearch optionFilterProp="label" options={statusOptions} placeholder="Выберите статус" />
                        </Form.Item>
                        <Form.Item
                            name="hours"
                            label="Дольше"
                            rules={[{ required: true, message: 'Укажите срок' }]}
                            style={{ minWidth: 130 }}
                        >
                            <InputNumber min={0} max={8760} addonAfter="ч" style={{ width: '100%' }} />
                        </Form.Item>
                        <Form.Item
                            name="minutes"
                            label="и ещё"
                            style={{ minWidth: 130 }}
                            extra="необязательно"
                        >
                            <InputNumber min={0} max={59} addonAfter="мин" style={{ width: '100%' }} />
                        </Form.Item>
                    </Space>

                    <Form.Item
                        name="to_status"
                        label="Перевести в статус"
                        extra="Оставьте пустым, если правило должно только ставить задачу"
                    >
                        <Select allowClear showSearch optionFilterProp="label" options={statusOptions} placeholder="Не менять статус" />
                    </Form.Item>

                    <Form.Item name="create_task" label="Ставить автозадачу ответственному" valuePropName="checked">
                        <Switch />
                    </Form.Item>

                    {createTask && (
                        <>
                            <Form.Item name="task_title" label="Название задачи">
                                <Input placeholder="Проверь чат с агентом и актуализируй статус" />
                            </Form.Item>
                            <Form.Item name="task_text" label="Текст задачи">
                                <Input.TextArea rows={3} placeholder="Поставь задачу, чтобы не забыть про лида" />
                            </Form.Item>
                        </>
                    )}
                </Form>
            </Modal>
        </Spin>
    )
}
